import { useState, useMemo, useEffect, type KeyboardEvent } from 'react'
import { encodeFunctionData, type Address, type PublicClient, type WalletClient } from 'viem'
import { LSP0_ABI, PERMISSION_CONTROL } from '../constants'
import { getSkillMarkdownAbsoluteUrl, PERMISSION_NAMES } from '../constants'
import {
  decodeAllowedCalls,
  decodeAllowedDataKeys,
  decodePermissions,
  describeAllowedCallTuple,
  describeDataKeyPrefix,
  fetchProfileFromIndexer,
  formatAddress,
  formatAllowedDataKeyPreview,
  resolveAllowedCallFunctionLabel,
  resolveAllowedCallTargetLabel,
  type AllowedCall,
} from '../utils'
import type { RegistryTemplate, TemplateMetadata } from '../hooks/useTemplateRegistry'
import { LuksoProfileAvatar } from './LuksoProfileAvatar'
import { PERMISSION_CONTROL_ABI } from '../contracts/permissionControl'
import type { PublisherTemplateFormInitialValues } from './PublisherTemplateForm'

export interface TemplateMarketplaceProps {
  templates: RegistryTemplate[]
  getMetadataForTemplate: (t: RegistryTemplate) => TemplateMetadata | null
  status: 'idle' | 'loading' | 'ready' | 'error'
  error: string | null
  isConnected: boolean
  onInstall: (t: RegistryTemplate, meta: TemplateMetadata | null) => void | Promise<void>
  onAddTemplate?: () => void
  isConnecting?: boolean
  isExtensionAvailable?: boolean
  isWalletConnectAvailable?: boolean
  connectError?: string | null
  onConnectExtension?: () => void
  onConnectWalletConnect?: () => void
  /** Used to resolve contract names on the template detail page */
  publicClient?: PublicClient | null
  upAddress?: Address | null
  walletClient?: WalletClient | null
  onCreateNewVersion?: (initialValues: PublisherTemplateFormInitialValues) => void
  onTemplateActionSuccess?: () => void
}

export function TemplateMarketplace({
  templates,
  getMetadataForTemplate,
  status,
  error,
  isConnected,
  onInstall,
  onAddTemplate,
  isConnecting,
  isExtensionAvailable,
  isWalletConnectAvailable,
  connectError,
  onConnectExtension,
  onConnectWalletConnect,
  publicClient = null,
  upAddress = null,
  walletClient = null,
  onCreateNewVersion,
  onTemplateActionSuccess,
}: TemplateMarketplaceProps) {
  const [search, setSearch] = useState('')
  const [selectedTemplate, setSelectedTemplate] = useState<RegistryTemplate | null>(null)
  const [agentCurlCopied, setAgentCurlCopied] = useState(false)
  const skillMarkdownAbsoluteUrl = getSkillMarkdownAbsoluteUrl()
  const agentCurlCommand = `curl -s ${skillMarkdownAbsoluteUrl}`

  const filteredTemplates = useMemo(() => {
    if (!search.trim()) return templates
    const q = search.trim().toLowerCase()
    return templates.filter((t) => {
      const meta = getMetadataForTemplate(t)
      const name = (meta?.name || meta?.app?.name || '').toLowerCase()
      const desc = (meta?.description || '').toLowerCase()
      const app = (meta?.app?.name || '').toLowerCase()
      return name.includes(q) || desc.includes(q) || app.includes(q)
    })
  }, [templates, search, getMetadataForTemplate])

  if (!isConnected) {
    const hasConnectActions = onConnectExtension || onConnectWalletConnect
    return (
      <div className="py-16">
        <div className="text-center">
          <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">Templates</h1>
          <p className="text-gray-400 mb-8 max-w-md mx-auto">
            Permission templates for your Universal Profile
          </p>

          {connectError && (
            <div className="mb-4 p-3 rounded-xl border border-red-500/30 bg-red-500/10 text-red-300 text-sm max-w-md mx-auto">
              {connectError}
            </div>
          )}

          {hasConnectActions && (
            <div className="flex flex-col gap-3 max-w-sm mx-auto">
              {isExtensionAvailable && onConnectExtension && (
                <button
                  onClick={onConnectExtension}
                  disabled={isConnecting}
                  className="btn-primary inline-flex items-center justify-center gap-2 w-full"
                >
                  {isConnecting ? (
                    <>
                      <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Connecting...
                    </>
                  ) : (
                    <>Connect Universal Profile</>
                  )}
                </button>
              )}
              {isWalletConnectAvailable && onConnectWalletConnect && (
                <button
                  onClick={onConnectWalletConnect}
                  disabled={isConnecting}
                  className="btn-secondary inline-flex items-center justify-center gap-2 w-full"
                >
                  WalletConnect
                </button>
              )}
            </div>
          )}

          <div className="mt-24 max-w-sm mx-auto text-left">
            <p className="text-[11px] font-medium tracking-wide text-gray-500 uppercase mb-3 text-center">
              Skill for your agent
            </p>
            <div className="flex rounded-xl border border-white/[0.08] bg-white/[0.04] overflow-hidden">
              <pre className="flex-1 min-w-0 overflow-x-auto pl-4 pr-2 py-3.5 font-mono text-[11px] sm:text-xs leading-relaxed text-gray-300 whitespace-pre">
                {agentCurlCommand}
              </pre>
              <div className="flex items-stretch border-l border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard.writeText(agentCurlCommand).then(() => {
                      setAgentCurlCopied(true)
                      window.setTimeout(() => setAgentCurlCopied(false), 2000)
                    })
                  }}
                  className="shrink-0 px-3 sm:px-4 flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:bg-white/5 hover:text-gray-200 transition-colors"
                  title="Copy curl command"
                >
                  <svg className="w-3.5 h-3.5 opacity-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  {agentCurlCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>

            {/* <p className="text-[11px] font-medium tracking-wide text-gray-500 uppercase mb-3 mt-8 text-center">
              Contract ABI (JSON)
            </p>
            <div className="flex rounded-xl border border-white/[0.08] bg-white/[0.04] overflow-hidden">
              <pre className="flex-1 min-w-0 overflow-x-auto pl-4 pr-2 py-3.5 font-mono text-[11px] sm:text-xs leading-relaxed text-gray-300 whitespace-pre">
                {agentAbiCurlCommand}
              </pre>
              <div className="flex items-stretch border-l border-white/10">
                <button
                  type="button"
                  onClick={() => {
                    void navigator.clipboard.writeText(agentAbiCurlCommand).then(() => {
                      setAgentAbiCurlCopied(true)
                      window.setTimeout(() => setAgentAbiCurlCopied(false), 2000)
                    })
                  }}
                  className="shrink-0 px-3 sm:px-4 flex items-center gap-1.5 text-xs font-medium text-gray-400 hover:bg-white/5 hover:text-gray-200 transition-colors"
                  title="Copy curl command"
                >
                  <svg className="w-3.5 h-3.5 opacity-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                    />
                  </svg>
                  {agentAbiCurlCopied ? 'Copied' : 'Copy'}
                </button>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-3 text-center leading-relaxed">
              Same ABI this app uses (viem/ethers-ready JSON array).
            </p> */}
          </div>
        </div>
      </div>
    )
  }

  // Detail view (Linear-style single integration page)
  if (selectedTemplate) {
    const meta = getMetadataForTemplate(selectedTemplate)
    return (
      <TemplateDetailView
        template={selectedTemplate}
        meta={meta}
        onBack={() => setSelectedTemplate(null)}
        onInstall={() => onInstall(selectedTemplate, meta)}
        publicClient={publicClient}
        upAddress={upAddress}
        walletClient={walletClient}
        onCreateNewVersion={onCreateNewVersion}
        onTemplateActionSuccess={onTemplateActionSuccess}
      />
    )
  }

  // List view: content section header (top row = CTA), then title row, then main content
  return (
    <div className="space-y-6">
      {/* Top row: CTA only */}
      {onAddTemplate && (
        <div className="flex justify-end w-full">
          <button
            type="button"
            onClick={onAddTemplate}
            className="btn-primary text-sm inline-flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add template
          </button>
        </div>
      )}

      {/* Title row */}
      <h1 className="text-2xl lg:text-3xl font-bold text-white pt-8">Templates</h1>

      {/* Main content */}
      <div className="space-y-6">
        <p className="text-gray-400">
          Enhance your Universal Profile with permission templates from apps and publishers.
        </p>

        <div className="relative w-full">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </span>
          <input
            type="search"
            placeholder="Search templates"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 py-2.5 pr-4 rounded-lg text-sm bg-surface border border-white/10 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-gray-500/50 focus:border-white/15"
            aria-label="Search templates"
          />
        </div>
      </div>

      {status === 'loading' || status === 'idle' ? (
        <div className="rounded-xl border border-white/5 bg-surface p-8 flex items-center justify-center min-h-[200px]">
          <p className="text-gray-400">Loading templates from registry…</p>
        </div>
      ) : status === 'error' ? (
        <div className="rounded-xl border border-white/5 bg-surface p-6">
          <p className="text-red-300">Failed to load templates from registry.</p>
          {error && (
            <p className="mt-2 text-xs text-gray-500 break-words overflow-wrap-anywhere">{error}</p>
          )}
        </div>
      ) : filteredTemplates.length === 0 ? (
        <div className="rounded-xl border border-white/5 bg-surface p-8 text-center">
          <p className="text-gray-400 mb-4">
            {search.trim() ? 'No templates match your search.' : 'No templates published yet.'}
          </p>
          {onAddTemplate && !search.trim() && (
            <button type="button" onClick={onAddTemplate} className="btn-primary text-sm">
              Add template
            </button>
          )}
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {filteredTemplates.map((t) => {
            const meta = getMetadataForTemplate(t)
            return (
              <TemplateGridCard
                key={`${t.publisherUP}-${t.templateId}-${t.version}`}
                template={t}
                meta={meta}
                onOpen={() => setSelectedTemplate(t)}
                onInstall={() => onInstall(t, meta)}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}

function usePublisherProfile(address: string) {
  const [profile, setProfile] = useState<{ name: string | null; profileImageUrl: string | null } | null>(null)

  useEffect(() => {
    let cancelled = false
    fetchProfileFromIndexer(address).then((p) => {
      if (!cancelled) setProfile(p)
    })
    return () => {
      cancelled = true
    }
  }, [address])

  return profile
}

function normalizeWebsiteHref(raw: string | undefined): string | null {
  const t = raw?.trim()
  if (!t) return null
  if (/^https?:\/\//i.test(t)) return t
  return `https://${t}`
}

/** Full-width card: publisher avatar on its own row, then copy; Add CTA below on small screens / beside on sm+ */
function TemplateGridCard({
  template,
  meta,
  onOpen,
  onInstall,
}: {
  template: RegistryTemplate
  meta: TemplateMetadata | null
  onOpen: () => void
  /** Opens add-controller flow with this template (Hub), same as detail Install */
  onInstall: () => void
}) {
  const publisherProfile = usePublisherProfile(template.publisherUP)
  const publisherName = publisherProfile?.name?.trim() || formatAddress(template.publisherUP, 4)

  const appName = meta?.app?.name?.trim()
  const templateName = meta?.name?.trim()
  const heroTitle = appName || templateName || 'Template'
  const subtitle =
    appName && templateName && templateName !== appName ? templateName : undefined

  const description = meta?.description || 'Permission template for your Universal Profile.'
  const permissionNames = decodePermissions(template.permissions.toString())
  const permCount = permissionNames.length
  const websiteHref = normalizeWebsiteHref(meta?.app?.website)

  const openKeyboard = (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onOpen()
    }
  }

  return (
    <div className="w-full rounded-xl border border-white/[0.08] bg-surface p-4 sm:p-5 hover:bg-white/[0.04] hover:border-white/10 transition-all duration-200 focus-within:ring-2 focus-within:ring-cta-primary/40 focus-within:ring-offset-2 focus-within:ring-offset-lukso-dark">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-4">
        {/* div (not role=button) so an external link can sit next to the title without invalid nesting */}
        <div
          tabIndex={0}
          onClick={onOpen}
          onKeyDown={openKeyboard}
          className="flex flex-col gap-3 flex-1 min-w-0 w-full text-left cursor-pointer outline-none rounded-lg focus-visible:ring-2 focus-visible:ring-cta-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-surface"
          aria-label={`View template ${heroTitle}`}
        >
          <div className="flex-shrink-0">
            <LuksoProfileAvatar
              address={template.publisherUP}
              profileUrl={publisherProfile?.profileImageUrl}
              size="lg"
            />
          </div>
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h3 className="text-lg font-semibold text-white leading-snug">{heroTitle}</h3>
              {websiteHref && (
                <a
                  href={websiteHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors rounded-md py-0.5"
                  onClick={(e) => e.stopPropagation()}
                  onKeyDown={(e) => e.stopPropagation()}
                >
                  <span className="sr-only">Website</span>
                  <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                    />
                  </svg>
                </a>
              )}
              {template.deprecated && (
                <span className="text-[10px] px-1.5 py-0.5 rounded border border-red-500/40 bg-red-500/10 text-red-300">
                  Deprecated
                </span>
              )}
              <span className="text-xs text-gray-500">v{template.version}</span>
            </div>
            {subtitle && <p className="text-sm text-gray-400">{subtitle}</p>}
            <p className="text-sm text-gray-400 line-clamp-3 sm:line-clamp-2">{description}</p>
            <p className="text-sm text-gray-400 break-words">
              <span className="text-gray-500">Publisher:</span>{' '}
              <span className="text-gray-300 break-words" title={template.publisherUP}>
                {publisherName}
              </span>
            </p>
            <p className="text-xs text-gray-500">
              {permCount} permission{permCount === 1 ? '' : 's'}
              {permCount > 0 && (
                <span className="text-gray-600">
                  {' '}
                  · {permissionNames.slice(0, 3).map((p) => PERMISSION_NAMES[p] || p).join(', ')}
                  {permCount > 3 ? '…' : ''}
                </span>
              )}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={onInstall}
          disabled={template.deprecated}
          title={
            template.deprecated ? 'Deprecated templates cannot be installed' : 'Add controller with this template'
          }
          className="btn-primary text-sm inline-flex items-center justify-center w-full sm:w-auto sm:min-w-[5.5rem] sm:flex-shrink-0 sm:self-start disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Add
        </button>
      </div>
    </div>
  )
}

function AllowedCallDetailRow({
  call,
  idx,
  publicClient,
}: {
  call: AllowedCall
  idx: number
  publicClient: PublicClient | null
}) {
  const row = describeAllowedCallTuple(call)
  const [target, setTarget] = useState<{ primary: string; secondary?: string } | null>(null)
  const [fnLabel, setFnLabel] = useState<{ primary: string; secondary?: string } | null>(null)

  useEffect(() => {
    let cancelled = false
    Promise.all([
      resolveAllowedCallTargetLabel(publicClient, call),
      resolveAllowedCallFunctionLabel(call.functionSelector),
    ]).then(([t, f]) => {
      if (!cancelled) {
        setTarget(t)
        setFnLabel(f)
      }
    })
    return () => {
      cancelled = true
    }
  }, [publicClient, call.address, call.callTypes, call.interfaceId, call.functionSelector])

  return (
    <li className="rounded-lg border border-white/[0.08] bg-white/[0.03] p-3 text-sm">
      <div className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">
        Call {idx + 1}
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
        <div className="sm:col-span-2">
          <dt className="text-gray-500">Call types</dt>
          <dd className="font-mono text-gray-200 mt-0.5">{row.callTypes}</dd>
        </div>
        <div>
          <dt className="text-gray-500">Target</dt>
          <dd className="text-gray-200 mt-0.5 break-words" title={call.address}>
            {target ? (
              <>
                <span className="text-gray-200">{target.primary}</span>
                {target.secondary && (
                  <span className="block font-mono text-gray-500 text-[11px] mt-0.5">{target.secondary}</span>
                )}
              </>
            ) : (
              <span className="text-gray-500">Resolving…</span>
            )}
          </dd>
        </div>
        <div>
          <dt className="text-gray-500">Interface</dt>
          <dd className="text-gray-200 mt-0.5 break-all font-mono">{row.interfaceId}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-gray-500">Function</dt>
          <dd className="text-gray-200 mt-0.5 break-words" title={call.functionSelector}>
            {fnLabel ? (
              <>
                <span className="text-gray-200">{fnLabel.primary}</span>
                {fnLabel.secondary && (
                  <span className="block font-mono text-gray-500 text-[11px] mt-0.5">{fnLabel.secondary}</span>
                )}
              </>
            ) : (
              <span className="text-gray-500">Resolving…</span>
            )}
          </dd>
        </div>
      </dl>
    </li>
  )
}

/** Template detail — avatar on its own row, then title/meta; actions column unchanged */
function TemplateDetailView({
  template,
  meta,
  onBack,
  onInstall,
  publicClient,
  upAddress,
  walletClient,
  onCreateNewVersion,
  onTemplateActionSuccess,
}: {
  template: RegistryTemplate
  meta: TemplateMetadata | null
  onBack: () => void
  onInstall: () => void
  publicClient?: PublicClient | null
  upAddress?: Address | null
  walletClient?: WalletClient | null
  onCreateNewVersion?: (initialValues: PublisherTemplateFormInitialValues) => void
  onTemplateActionSuccess?: () => void
}) {
  const permissionNames = decodePermissions(template.permissions.toString()).map(
    (p) => PERMISSION_NAMES[p] || p
  )
  const decodedAllowedCalls = decodeAllowedCalls(template.allowedCalls)
  const decodedAllowedDataKeys = decodeAllowedDataKeys(template.allowedERC725YDataKeys)
  const publisherProfile = usePublisherProfile(template.publisherUP)
  const publisherName = publisherProfile?.name?.trim() || formatAddress(template.publisherUP, 4)

  const appName = meta?.app?.name?.trim()
  const templateName = meta?.name?.trim()
  const heroTitle = appName || templateName || 'Template'
  const subtitle =
    appName && templateName && templateName !== appName ? templateName : undefined
  const websiteHref = normalizeWebsiteHref(meta?.app?.website)
  const fullDescription = meta?.description || ''
  const isOwner = !!upAddress && upAddress.toLowerCase() === template.publisherUP.toLowerCase()
  const canCreateNewVersion = isOwner && !!onCreateNewVersion && !!meta?.template?.slug
  const [isDeprecating, setIsDeprecating] = useState(false)
  const [deprecateError, setDeprecateError] = useState<string | null>(null)

  const pc = publicClient ?? null

  const createNewVersion = () => {
    if (!onCreateNewVersion) return
    const slug = meta?.template?.slug?.trim()
    if (!slug) return
    onCreateNewVersion({
      slug,
      version: template.version + 1,
      permissions: template.permissions,
      allowedCallsHex: template.allowedCalls,
      allowedDataKeysHex: template.allowedERC725YDataKeys,
      templateName: meta?.name?.trim() || '',
      appName: meta?.app?.name?.trim() || '',
      controllerAddress: meta?.app?.controllerAddress?.trim() || '',
      templateDescription: meta?.description || '',
      appWebsite: meta?.app?.website || '',
      docsUrl: meta?.app?.docs || '',
      brandColor: meta?.branding?.primaryColor || '',
    })
  }

  const deprecateTemplate = async () => {
    if (!upAddress || !walletClient || !pc || !isOwner || template.deprecated) return
    setDeprecateError(null)
    setIsDeprecating(true)
    try {
      const deprecateData = encodeFunctionData({
        abi: PERMISSION_CONTROL_ABI,
        functionName: 'deprecateTemplate',
        args: [template.tokenId, true],
      })

      const hash = await walletClient.writeContract({
        address: upAddress,
        abi: LSP0_ABI,
        functionName: 'execute',
        args: [0n, PERMISSION_CONTROL.address, 0n, deprecateData],
        account: upAddress,
        chain: walletClient.chain,
      })
      const receipt = await pc.waitForTransactionReceipt({ hash })
      if (receipt.status !== 'success') {
        throw new Error('Deprecation transaction failed')
      }
      onTemplateActionSuccess?.()
    } catch (err) {
      setDeprecateError(err instanceof Error ? err.message : 'Failed to deprecate template')
    } finally {
      setIsDeprecating(false)
    }
  }

  return (
    <div className="max-w-3xl">
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white mb-6 transition-colors focus:outline-none focus:ring-2 focus:ring-cta-primary/40 focus:ring-offset-2 focus:ring-offset-lukso-dark rounded"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
        </svg>
        Templates
      </button>

      <div className="rounded-xl border border-white/[0.08] bg-surface overflow-hidden">
        <div className="p-6 lg:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-4">
            <div className="flex flex-col gap-3 flex-1 min-w-0 w-full">
              <div className="flex-shrink-0">
                <LuksoProfileAvatar
                  address={template.publisherUP}
                  profileUrl={publisherProfile?.profileImageUrl}
                  size="lg"
                />
              </div>
              <div className="min-w-0 space-y-2">
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <h1 className="text-lg font-semibold text-white leading-snug">{heroTitle}</h1>
                  {websiteHref && (
                    <a
                      href={websiteHref}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors rounded-md py-0.5"
                    >
                      <span className="sr-only">Website</span>
                      <svg className="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                        />
                      </svg>
                    </a>
                  )}
                  {template.deprecated && (
                    <span className="text-[10px] px-1.5 py-0.5 rounded border border-red-500/40 bg-red-500/10 text-red-300">
                      Deprecated
                    </span>
                  )}
                  <span className="text-xs text-gray-500">v{template.version}</span>
                </div>
                {subtitle && <p className="text-sm text-gray-400">{subtitle}</p>}
                {meta?.app?.controllerAddress && (
                  <p className="text-sm text-gray-500 break-words">
                    Controller:{' '}
                    <span className="font-mono text-gray-400 break-all sm:break-normal" title={meta.app.controllerAddress}>
                      {formatAddress(meta.app.controllerAddress, 6)}
                    </span>
                  </p>
                )}
                <p className="text-sm text-gray-400 break-words">
                  <span className="text-gray-500">Publisher:</span>{' '}
                  <span className="text-gray-300 break-words" title={template.publisherUP}>
                    {publisherName}
                  </span>
                </p>
                {meta?.app?.docs && (
                  <a
                    href={meta.app.docs}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-gray-400 hover:text-white transition-colors"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                    Docs
                  </a>
                )}
              </div>
            </div>
            <div className="flex flex-col items-stretch gap-2 w-full sm:w-auto sm:min-w-[5.5rem] sm:flex-shrink-0 sm:self-start">
              <button
                type="button"
                className="btn-primary text-sm inline-flex items-center justify-center w-full sm:w-auto"
                onClick={onInstall}
                disabled={template.deprecated}
                title={
                  template.deprecated ? 'Deprecated templates cannot be installed' : 'Add controller with this template'
                }
              >
                Add
              </button>
              {isOwner && (
                <>
                  <button
                    type="button"
                    className="btn-secondary text-sm inline-flex items-center justify-center w-full sm:w-auto"
                    onClick={createNewVersion}
                    disabled={!canCreateNewVersion}
                    title={!meta?.template?.slug ? 'Template slug is required in metadata to create a new version' : undefined}
                  >
                    Create new version
                  </button>
                </>
              )}
            </div>
          </div>
          {deprecateError && (
            <p className="mt-3 text-xs text-red-300 break-words">{deprecateError}</p>
          )}
        </div>

        {/* Optional visual / banner */}
        {meta?.branding?.banner && (
          <div className="border-t border-white/5">
            <img
              src={meta.branding.banner}
              alt=""
              className="w-full h-40 object-cover object-center bg-white/5"
            />
          </div>
        )}

        {/* Overview */}
        <div className="border-t border-white/5 p-6 lg:p-8">
          <h3 className="text-sm font-semibold text-white mb-2">Overview</h3>
          <p className="text-sm text-gray-400 leading-relaxed whitespace-pre-wrap">{fullDescription}</p>
        </div>

        {/* Permissions */}
        {permissionNames.length > 0 && (
          <div className="border-t border-white/5 p-6 lg:p-8">
            <h3 className="text-sm font-semibold text-white mb-3">Permissions</h3>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap">
              {permissionNames.map((p) => (
                <span
                  key={p}
                  className="text-xs px-2.5 py-1.5 rounded-lg border border-white/10 bg-white/[0.04] text-gray-300 w-full sm:w-auto text-left break-words"
                >
                  {p}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="border-t border-white/5 p-6 lg:p-8">
          <h3 className="text-sm font-semibold text-white mb-3">Allowed calls</h3>
          {decodedAllowedCalls.length === 0 ? (
            <p className="text-sm text-gray-400">
              No scoped AllowedCalls — on-chain <span className="font-mono text-gray-500">allowedCalls</span> is empty.
              Broader call permissions may still apply without per-contract scoping.
            </p>
          ) : (
            <ul className="space-y-3">
              {decodedAllowedCalls.map((call, idx) => (
                <AllowedCallDetailRow
                  key={`${idx}-${call.address}-${call.functionSelector}`}
                  call={call}
                  idx={idx}
                  publicClient={pc}
                />
              ))}
            </ul>
          )}
        </div>

        {decodedAllowedDataKeys.length > 0 && (
          <div className="border-t border-white/5 p-6 lg:p-8">
            <h3 className="text-sm font-semibold text-white mb-3">Allowed set data</h3>
            <ul className="space-y-2">
              {decodedAllowedDataKeys.map((k) => {
                const prefixLabel = describeDataKeyPrefix(k)
                return (
                  <li
                    key={k}
                    className="rounded-lg border border-white/[0.08] bg-white/[0.03] px-3 py-2 text-xs"
                    title={k}
                  >
                    {prefixLabel && (
                      <div className="text-sm text-gray-200 mb-1">{prefixLabel}</div>
                    )}
                    <span className="font-mono text-gray-400 break-all">{formatAllowedDataKeyPreview(k, 14)}</span>
                  </li>
                )
              })}
            </ul>
          </div>
        )}

        {/* Meta footer */}
        <div className="border-t border-white/5 px-6 lg:px-8 py-3 flex flex-wrap items-center gap-4 text-xs text-gray-500">
          <span>Version {template.version}</span>
          <span>Chain ID {template.chainId.toString()}</span>
          {template.deprecated && (
            <span className="text-red-400">Deprecated</span>
          )}
        </div>
      </div>

      {isOwner && !template.deprecated && (
        <div className="pt-3">
          <button
            type="button"
            className="text-sm text-red-400 hover:text-red-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => void deprecateTemplate()}
            disabled={isDeprecating}
            title="Deprecate this template"
          >
            {isDeprecating ? 'Deprecating…' : 'Deprecate template'}
          </button>
        </div>
      )}
    </div>
  )
}

